import React, { Component } from 'react';
import { connect } from 'react-redux';
import moment from 'moment';
import { Modal } from 'antd';
import ScenarioCompareDrawerComponent from '../components/ScenarioComparison/ScenarioCompareDrawer';
import ScenarioCompareSummaryModal from '../components/ScenarioComparison/ScenarioCompareSummaryModal';
import baseActions from '../actions';
import { mapGlobalFilterKeysToObj } from '../components/Filters';
import {
  SCENARIOS_COMPARE_KPIS, SCENARIOS_COMPARE_DATA, RAW_DATA_TYPES, GLOBAL_FILTERS,
  SCENARIOS_COMPARE_DATA_SUMMARY, PERSIST_PARAMS, KPI_REPORT_PICK_VOLUME, KPI_COST_SUMMARY,
  KPI_UTILISATION_REPORT, KPI_FACILITY_REPORT, KPI_STORAGE_REPORT, KPI_PRODUCTIVITY_REPORT, URLS
} from '../plannerConfig';
import { getFilters } from '../helpers';
import PlanningKPIReport from '../components/ScenarioComparison/PlanningKpiReport';
import { ROUTE_DATE_FORMAT } from '../constants/app.constants';

const CUT_OFF_RATE = 100;

const getWeekDayFromStartDate = (date, weekDaySelected) => moment(date)
  // Week day selected is not 0 based hence we subtract 1.
  .add(weekDaySelected - 1, 'd')
  .format(ROUTE_DATE_FORMAT);

const getComparisonDates = (startDate, endDate, weekDaySelected) => {
  const parsedStartDate = weekDaySelected
    ? getWeekDayFromStartDate(startDate, weekDaySelected)
    : startDate;
  const parsedEndDate = weekDaySelected
    ? parsedStartDate
    : endDate;
  return {
    startDate: parsedStartDate,
    endDate: parsedEndDate,
  };
};

const mapStateToProps = state => ({
  [SCENARIOS_COMPARE_KPIS]: state[SCENARIOS_COMPARE_KPIS],
  [SCENARIOS_COMPARE_DATA]: state[SCENARIOS_COMPARE_DATA],
  [SCENARIOS_COMPARE_DATA_SUMMARY]: state[SCENARIOS_COMPARE_DATA_SUMMARY],
  [RAW_DATA_TYPES]: state[RAW_DATA_TYPES].data,
  [GLOBAL_FILTERS]: state[GLOBAL_FILTERS].data,
  [PERSIST_PARAMS]: state[PERSIST_PARAMS].data,
  [KPI_REPORT_PICK_VOLUME]: state[KPI_REPORT_PICK_VOLUME],
  [KPI_COST_SUMMARY]: state[KPI_COST_SUMMARY],
  [KPI_UTILISATION_REPORT]: state[KPI_UTILISATION_REPORT],
  [KPI_FACILITY_REPORT]: state[KPI_FACILITY_REPORT],
  [KPI_STORAGE_REPORT]: state[KPI_STORAGE_REPORT],
  [KPI_PRODUCTIVITY_REPORT]: state[KPI_PRODUCTIVITY_REPORT],
});

const mapDispatchToProps = {
  get: baseActions.get,
  set: baseActions.set,
  post: baseActions.post,
};

const gridConfigForKpis = {
  includeRowCounts: false,
  includeItemSelection: false,
  includePagination: false,
  includeEditDeleteCell: false,
  includeToolbar: false,
  hiddenColumns: ['id', 'Description'],
};

const gridConfigForData = {
  includeRowCounts: false,
  includeItemSelection: false,
  includePagination: false,
  includeEditDeleteCell: false,
  includeToolbar: false,
  showFiltersOnMount: false,
  hiddenColumns: ['id'],
};

class ScenarioCompareDrawer extends Component {
  state = {
    weekDaySelect: 0,
    dataType: 'activity_profile',
    inputOrOutput: 'output',
    globalFilterApplied: [],
    kpiFilterApplied: [],
    dataCutOff: true,
    modalVisible: false,
  }

  componentDidMount() {
    const { selectedScenarios, rawDataTypes, get } = this.props;
    if (rawDataTypes.length === 0) get([RAW_DATA_TYPES]);
    getFilters(this.props, selectedScenarios[0].id, selectedScenarios[1].id);
    this.updateData('kpiOnly');
    this.updateData('dataOnly', true);
    this.getKpiReport();
  }

  handleCancel = () => this.setState({ modalVisible: false });

  handleClickToShowAllData = () => this.updateData('dataOnly', false);

  handleClickToShowDataSummary = () => {
    this.updateData('dataSummaryOnly');
    this.setState({ modalVisible: true });
  }

  handleClickToChangeWeekDaySelect = weekDaySelect => this.changeParamsUpdateData(
    { weekDaySelect }, true, true,
  );

  handleClickToChangeFilter = (globalFilterHook, kpiFilterHook) => this.changeParamsUpdateData(
    {
      globalFilterApplied: globalFilterHook,
      kpiFilterApplied: kpiFilterHook,
    }, true,
  );

  handleClickToChangeInputOutput = picked => this.changeParamsUpdateData(
    { inputOrOutput: picked.target.value }, true,
  );

  handleClickToChangeDataType = dataType => this.changeParamsUpdateData(
    { dataType }, false, true,
  );

  handleClickToToggleFilterView = () => {
    const { set, persistParams } = this.props;
    set([PERSIST_PARAMS], { hideFilterView: !persistParams.hideFilterView });
  }

  handleCSVDownload = () => {
    const {
      selectedScenarios, startDate, endDate,
    } = this.props;
    const {
      weekDaySelect, dataType,
    } = this.state;

    const routeParams = {
      scenario: selectedScenarios[0].id,
      scenarioCompare: selectedScenarios[1].id,
      dataType,
      ...getComparisonDates(startDate, endDate, weekDaySelect),
    };

    window.open(URLS.xlsx.downloadScenarioComparison(routeParams));
  }

  changeParamsUpdateData = (params, kpiUpdate, dataUpdate) => {
    const { scenariosCompareKpis, scenariosCompareData } = this.props;
    this.setState({
      ...params,
    }, () => {
      // Ensure multiple endpoint calls doesn't happen.
      if (scenariosCompareKpis.loadingQueue.length === 0) {
        this.updateData(kpiUpdate && 'kpiOnly');
      }

      if (scenariosCompareData.loadingQueue.length === 0) {
        this.updateData(dataUpdate && 'dataOnly', true);
      }

      // Refresh the Kpi report.
      this.getKpiReport();
    });
  }

  updateData = (type, cutOff) => {
    const {
      get, post, selectedScenarios, startDate, endDate,
    } = this.props;
    const {
      weekDaySelect, inputOrOutput, dataType, globalFilterApplied, kpiFilterApplied,
    } = this.state;

    let call = (arg1, arg2) => get(arg1, arg2);
    let body; // Only post would have a body.
    let seq = [];
    switch (type) {
      case 'kpiOnly': {
        seq = [SCENARIOS_COMPARE_KPIS];
        call = (arg1, arg2) => post(arg1, arg2);
        body = ((globalFilterApplied.length + kpiFilterApplied.length) > 0)
          ? mapGlobalFilterKeysToObj([...globalFilterApplied, ...kpiFilterApplied])
          : {};
        break;
      }

      case 'dataOnly': {
        seq = [SCENARIOS_COMPARE_DATA];
        break;
      }

      case 'dataSummaryOnly': {
        seq = [SCENARIOS_COMPARE_DATA_SUMMARY];
        break;
      }

      default: {
        break;
      }
    }

    call(seq, {
      routeParams: {
        scenario: selectedScenarios[0].id,
        scenarioCompare: selectedScenarios[1].id,
        inputOrOutput,
        dataType,
        cutOff: cutOff && type === 'dataOnly' ? CUT_OFF_RATE : 0,
        ...getComparisonDates(startDate, endDate, weekDaySelect),
      },

      body,

      callback: (response) => {
        if (type === 'dataOnly') {
          const { data: { categories } } = response;
          const dataSum = Object.values(categories)
            .reduce((acc, obj) => acc + obj.count, 0);
          if (dataSum >= CUT_OFF_RATE) {
            this.setState({ dataCutOff: cutOff });
          } else { // Any data row sum is lower than cutoff rate, dataCutOff must be false.
            this.setState({ dataCutOff: false });
          }
        }
      },
    });
  }

  getKpiReport() {
    const {
      post, selectedScenarios, startDate, endDate,
    } = this.props;
    const { weekDaySelect, globalFilterApplied, kpiFilterApplied } = this.state;
    const body = ((globalFilterApplied.length + kpiFilterApplied.length) > 0)
      ? mapGlobalFilterKeysToObj([...globalFilterApplied, ...kpiFilterApplied])
      : {};
    const requestParameters = {
      routeParams: {
        scenarioA: selectedScenarios[0].id,
        scenarioB: selectedScenarios[1].id,
        ...getComparisonDates(startDate, endDate, weekDaySelect),
      },
      body,
    };
    [
      KPI_REPORT_PICK_VOLUME,
      KPI_COST_SUMMARY,
      KPI_UTILISATION_REPORT,
      KPI_FACILITY_REPORT,
      KPI_STORAGE_REPORT,
      KPI_PRODUCTIVITY_REPORT,
    ].forEach(entity => post([entity], requestParameters));
  }

  render() {
    const {
      selectedScenarios, scenariosCompareKpis, scenariosCompareData, scenariosCompareDataSummary,
      rawDataTypes, persistParams, globalFilters = {}, onClickToViewScenario, startDate,
      kpiPickVolume, kpiCostSummary, kpiUtilisationReport, kpiFacilityReport, kpiStorageReport,
      kpiProductivityReport, showKpiReport,
    } = this.props;

    const {
      weekDaySelect, dataType, globalFilterApplied, kpiFilterApplied,
      inputOrOutput, dataCutOff, modalVisible,
    } = this.state;

    const comparisonTitle = showKpiReport ? 'Planning KPI Report' : `KPI Differences (${inputOrOutput})`;

    return showKpiReport ? (
      <PlanningKPIReport
        data={{
          kpiPickVolume,
          kpiCostSummary,
          kpiUtilisationReport,
          kpiFacilityReport,
          kpiStorageReport,
          kpiProductivityReport,
        }}
        globalFilters={globalFilters}
        globalFilterApplied={globalFilterApplied}
        kpiFilterApplied={kpiFilterApplied}
        title={comparisonTitle}
        weekDaySelect={weekDaySelect}
        startDate={startDate}
        persistParams={persistParams}
        onClickToChangeWeekDaySelect={this.handleClickToChangeWeekDaySelect}
        onClickToChangeFilter={this.handleClickToChangeFilter}
      />
    ) : (
      <>
        <ScenarioCompareDrawerComponent
          startDate={startDate}
          selectedScenarios={selectedScenarios}
          weekDaySelect={weekDaySelect}
          inputOrOutput={inputOrOutput}
          dataType={dataType}
          downloadDataCompare={this.handleCSVDownload}
          globalFilters={globalFilters}
          globalFilterApplied={globalFilterApplied}
          kpiFilterApplied={kpiFilterApplied}
          onClickToViewScenario={onClickToViewScenario}
          onClickToChangeWeekDaySelect={this.handleClickToChangeWeekDaySelect}
          onClickToChangeDataType={this.handleClickToChangeDataType}
          onClickToChangeFilter={this.handleClickToChangeFilter}
          onClickToShowAllData={this.handleClickToShowAllData}
          onClickToShowDataSummary={this.handleClickToShowDataSummary}
          onClickToToggleFilterView={this.handleClickToToggleFilterView}
          onClickToChangeInputOutput={this.handleClickToChangeInputOutput}
          dataViewForKpis={scenariosCompareKpis}
          dataViewForData={scenariosCompareData}
          gridConfigForKpis={gridConfigForKpis}
          gridConfigForData={gridConfigForData}
          dataTypeOptions={rawDataTypes}
          dataCutOff={dataCutOff}
          persistParams={persistParams}
          title={comparisonTitle}
        />
        <Modal
          title="Summary of all data types"
          visible={modalVisible}
          onCancel={this.handleCancel}
          footer={null}
          centered
          width="90vw"
        >
          <ScenarioCompareSummaryModal
            dataViewForSummary={scenariosCompareDataSummary}
            gridConfigForData={gridConfigForData}
          />
        </Modal>
      </>
    );
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(ScenarioCompareDrawer);
